import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Accordion,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Grid,
  Icon,
  Input,
  Label,
  Message,
  MessageHeader,
  MessageItem,
  MessageList,
  Modal,
  Popup,
} from "semantic-ui-react";
import styled from "styled-components";
import {
  StreamFields,
  createDBCParser,
  fetchAllStreamsWithDetails,
  stopDBCParser,
  updateDBCSettings,
  uploadFile,
} from "../../../../BytebeamClient";
import { useUser } from "../../../../context/User.context";
import {
  DBCAdditionalSettings,
  DBCData,
  DBCResponse,
  DBCTypes,
  dropDownOptionsFromArray,
} from "../../../../util";
import { DuplicateSignalInfo, parseDBCFile } from "../../../../util/dbcParser";
import { beamtoast } from "../../../common/CustomToast";
import LoadingAnimation from "../../../common/Loader";
import { StyledInput as StyledFileInput } from "../../Actions/action-modals/UploadFirmwareComponent";
import { ThinDivider } from "../../Dashboards/Panel/util";
import { isUserAdmin } from "../../Dashboards/util";
import { StyledFileUploadButton } from "../../DeviceManagement/Devices/ActionModals/SendFileModal";
import AnimatedEllipsis from "../../common/AnimatedEllipsis";
import { Mixpanel } from "../../common/MixPanel";
import { DBCOperationType } from "./DBC";
import DuplicateSignalsWarning from "./DuplicateSignalsWarning";
import { pgnsWithLessThan8bytes } from "./constant";

export const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

export const StyledLabel = styled(Label)`
  margin: 0px !important;
  min-width: 80px;
`;

export const StyledInput = styled(Input)`
  margin-bottom: 14px;
  width: 100%;
`;

type Stream = {
  [key: string]: StreamFields;
};

interface UploadDBCFileSectionProps {
  readonly onClose: () => void;
  readonly DBCData: DBCResponse[];
  readonly newDBC: DBCData;
  readonly operationType: string;
  setIsEditOrCreateDBCGoingOn: (arg0: boolean) => void;
  setNewDBCOutputStream: (stream: string) => void;
  handleUpdate: () => void;
  duplicateSignals: DuplicateSignalInfo[];
  setDuplicateSignals: (signals: DuplicateSignalInfo[]) => void;
  allowDuplicateSignals: boolean;
  setAllowDuplicateSignals: (allowed: boolean) => void;
  isParsingDBC: boolean;
  setIsParsingDBC: (parsing: boolean) => void;
  parsingError: string | null;
  setParsingError: (error: string | null) => void;
}

interface HandleUploadDBCType {
  handleUpload: (additional_settings: DBCAdditionalSettings) => Promise<void>;
}

const UploadDBCFileSection = forwardRef<
  HandleUploadDBCType,
  UploadDBCFileSectionProps
>((props, ref) => {
  const {
    onClose,
    newDBC,
    operationType,
    setIsEditOrCreateDBCGoingOn,
    setNewDBCOutputStream,
    handleUpdate,
    duplicateSignals,
    setDuplicateSignals,
    allowDuplicateSignals,
    setAllowDuplicateSignals,
    isParsingDBC,
    setIsParsingDBC,
    parsingError,
    setParsingError,
  } = props;

  const [DBCFileName, setDBCFileName] = useState<string>("");
  const [DBCFile, setDBCFile] = useState<File>(new File([""], "filename"));
  const [showDBCUploadProgress, setShowDBCUploadProgress] =
    useState<boolean>(false);
  const [DBCFileLoaded, setDBCFileLoaded] = useState<number>(0);
  const [DBCFileTotal, setDBCFileTotal] = useState<number>(0);
  const [uploadedDBCFileResponse, setUploadedDBCFileResponse] = useState<{
    status: number;
    data: { id: string };
  }>({
    status: 0,
    data: { id: "" },
  });
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  // Reset functions for file states within this component
  const resetFileStates = useCallback(() => {
    setShowDBCUploadProgress(false);
    setDBCFileLoaded(0);
    setDBCFileTotal(0);
    setDBCFile(new File([""], "filename"));
    setDBCFileName("");
    setUploadedDBCFileResponse({
      status: 0,
      data: { id: "" },
    });
  }, []);

  const resetAllFileRelatedStatesLocal = useCallback(() => {
    resetFileStates();
    setParsingError(null);
    setDuplicateSignals([]);
    setAllowDuplicateSignals(false);
    setIsParsingDBC(false);
    setFileInputKey((prev) => prev + 1); // Force file input re-render
  }, [
    resetFileStates,
    setParsingError,
    setDuplicateSignals,
    setAllowDuplicateSignals,
    setIsParsingDBC,
  ]);

  const onSelect = useCallback(
    (e) => {
      if (e.target.files.length !== 0) {
        // Reset all file-related states first - completely replace old state
        resetAllFileRelatedStatesLocal();
        const selectedFile = e.target.files[0];
        if (selectedFile) {
          const fileName = selectedFile.name;
          const fileExtension = fileName.split(".").pop(); // Get the file extension

          // Check if the file extension is ".dbc"
          if (fileExtension !== "dbc") {
            beamtoast.error(`Only ".dbc" file types are allowed!`);
            console.log(
              'Only files with the ".dbc" extension are allowed to be uploaded.'
            );
            return;
          }

          // Check if the file is empty (size === 0)
          if (selectedFile.size === 0) {
            beamtoast.error("Selected DBC file is empty!");
            return;
          }

          // Completely replace any previous file data with the new file
          setDBCFile(selectedFile);
          setDBCFileName(fileName);

          // Parse DBC file to detect duplicate signals
          setIsParsingDBC(true);
          setParsingError(null);

          const reader = new FileReader();
          reader.onload = (e) => {
            try {
              const content = e.target?.result as string;
              if (content) {
                const parseResult = parseDBCFile(content);
                setDuplicateSignals(parseResult.duplicates);
                setAllowDuplicateSignals(false); // Reset to false for new file
                setParsingError(null);
              }
            } catch (error) {
              console.error("Error parsing DBC file:", error);
              const errorMessage = (error as Error).message || "Unknown error";
              let displayMessage =
                "Failed to parse DBC file. Please check the file format.";

              // Handle specific error cases from parseDBCFile
              if (errorMessage.includes("No CAN messages found in DBC file")) {
                displayMessage =
                  "Uploaded DBC file does not contain any CAN messages!";
              } else if (
                errorMessage.includes("No signals found in DBC file")
              ) {
                displayMessage =
                  "Uploaded DBC file does not contain any signals!";
              }

              setParsingError(displayMessage);
              setDuplicateSignals([]);
              beamtoast.error(displayMessage);
            } finally {
              setIsParsingDBC(false);
            }
          };

          reader.onerror = () => {
            setParsingError("Failed to read DBC file.");
            setIsParsingDBC(false);
            beamtoast.error("Failed to read DBC file.");
          };

          reader.readAsText(selectedFile);
        }
      } else {
        // If no file selected, reset all states
        resetAllFileRelatedStatesLocal();
      }
    },
    [
      resetAllFileRelatedStatesLocal,
      setAllowDuplicateSignals,
      setDuplicateSignals,
      setIsParsingDBC,
      setParsingError,
    ]
  );

  // function for all data upload/edit validation
  const handleUpload = useCallback(
    async (additional_settings: DBCAdditionalSettings) => {
      // no file upload is happening while edit mode is on
      // file validation: cannot be empty + .dbc required
      if (operationType === DBCOperationType.Create) {
        const hasFileName = DBCFileName !== "";
        const hasFile = DBCFile && DBCFile.size > 0;

        if (!hasFileName || !hasFile) {
          beamtoast.error("DBC file must be selected!");
          return;
        }
      }

      // Check for duplicate signals if not allowed
      if (duplicateSignals.length > 0 && !allowDuplicateSignals) {
        beamtoast.error(
          "Please allow duplicate signals or fix the duplicate signal names in your DBC file!"
        );
        return;
      }

      try {
        setShowDBCUploadProgress(true);

        // DBC Upload API Endpoint
        const url = `/api/v1/dbcs`;
        let formData = new FormData();

        // need to keep this check here itself due to ts strict typechecking
        if (
          newDBC.name &&
          newDBC.version &&
          newDBC.input_table &&
          newDBC.output_table &&
          newDBC.period &&
          additional_settings
        ) {
          formData.append("dbc_type", "custom");
          formData.append("file", DBCFile);
          formData.append("name", newDBC.name.trim());
          formData.append("version", newDBC.version.trim());
          formData.append("input_table", newDBC.input_table);
          formData.append("output_table", newDBC.output_table.trim());
          formData.append("period", newDBC.period.toString().trim());
          formData.append(
            "additional_settings",
            JSON.stringify(additional_settings)
          );
          formData.append(
            "allow_duplicate_signals",
            allowDuplicateSignals.toString()
          );
        } else {
          beamtoast.error("All fields are required.");
          return;
        }

        const res = await uploadFile(url, formData, (p) => {
          // for loading progress
          setDBCFileLoaded(p.loaded);
          setDBCFileTotal(p.total);
        });

        setUploadedDBCFileResponse(res);

        if (res.status === 201) {
          beamtoast.success(`Created DBC "${newDBC.name}"`);
          Mixpanel.track("Uploaded DBC", {
            DBC: res.data["version"],
          });
          setNewDBCOutputStream(newDBC.output_table ? newDBC.output_table : "");
          onClose();
          handleUpdate();
        }
      } catch (error: any) {
        console.log(error);
        setShowDBCUploadProgress(false);
        Mixpanel.track("Failure", {
          type: "Upload DBC",
        });
        setNewDBCOutputStream("");

        // Handling Error for File Size exceeding limit from NGINX
        if (String(error).includes("413")) {
          beamtoast.error("Upload failed due to size limit!");
        }

        // Handling other error types
        const errorMessage = error.response?.data?.error;

        if (errorMessage.includes("invalid continuation byte")) {
          beamtoast.error("Uploaded file is not supported!");
        } else if (errorMessage.includes("fixprotobuff failed")) {
          beamtoast.error("DBC created but fixprotobuff failed");
        } else if (errorMessage?.includes("No signals in DBC")) {
          beamtoast.error("Uploaded DBC file does not contain any signals!");
        } else {
          beamtoast.error(errorMessage, { duration: 6000 });
        }
      } finally {
        setIsEditOrCreateDBCGoingOn(false);
      }
    },
    [
      operationType,
      DBCFileName,
      DBCFile,
      duplicateSignals.length,
      allowDuplicateSignals,
      newDBC,
      setIsEditOrCreateDBCGoingOn,
      setNewDBCOutputStream,
      onClose,
      handleUpdate,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useImperativeHandle(ref, () => ({
    handleUpload,
  }));

  return (
    <Form>
      <Form.Field>
        <label htmlFor="upload_file">
          <p
            style={{
              fontSize: "1.1rem",
            }}
          >
            Upload file from the system
          </p>
        </label>
        <div style={{ position: "relative" }}>
          <StyledFileUploadButton
            fluid
            content="Select File"
            labelPosition="left"
            icon="file"
          />
          <StyledFileInput
            type="file"
            id="upload_file"
            onChange={onSelect}
            key={fileInputKey} // Force file input re-render
            accept=".dbc"
          />
        </div>
        <label htmlFor="file_name" style={{ marginTop: "12px" }}>
          File Chosen:{" "}
        </label>
        <span id="file_name">
          {DBCFileName !== "" ? DBCFileName : "No File Chosen"}
        </span>
      </Form.Field>

      {isParsingDBC && (
        <Form.Field>
          <Message info>
            <MessageHeader>
              <Icon name="spinner" loading />
              Parsing DBC File
            </MessageHeader>
            <MessageList>
              <MessageItem>
                Analyzing DBC file for duplicate signals...
              </MessageItem>
            </MessageList>
          </Message>
        </Form.Field>
      )}

      {parsingError && (
        <Form.Field>
          <Message error>
            <MessageHeader>
              <Icon name="exclamation triangle" />
              Parsing Error
            </MessageHeader>
            <MessageList>
              <MessageItem>{parsingError}</MessageItem>
            </MessageList>
          </Message>
        </Form.Field>
      )}
      {showDBCUploadProgress && (
        <Form.Field>
          <label htmlFor="file-progress">
            {uploadedDBCFileResponse.status === 0 ? (
              <span>
                File Uploading
                <AnimatedEllipsis spacing={3} dotSize={"8px"} />
              </span>
            ) : (
              <span>File Uploaded</span>
            )}
          </label>
          <progress
            id="file-progress"
            max={DBCFileTotal}
            value={DBCFileLoaded}
          />
        </Form.Field>
      )}
    </Form>
  );
});

interface CreateDBCModalProps {
  readonly open: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  readonly selectedDBC?: DBCResponse;
  readonly DBCData: DBCResponse[];
  readonly operationType: string;
  readonly setNewDBCOutputStream: (stream: string) => void;
  readonly handleUpdate: () => void;
}

export default function CreateOrEditDBCModal(props: CreateDBCModalProps) {
  const uploadDBCRef = useRef<HandleUploadDBCType>(null);
  const allStreams = useRef<string[]>([]);
  const validStreams = useRef<Stream>({});

  const { user } = useUser();
  const isAdmin = isUserAdmin(user);

  const [loading, setLoading] = useState<boolean>(false);
  const [isEditOrCreateDBCGoingOn, setIsEditOrCreateDBCGoingOn] =
    useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [newDBC, setNewDBC] = useState<DBCData>({
    dbc_type: "custom",
    name: "",
    version: "",
    input_table: "",
    output_table: "",
    period: 1000,
    obd_can_id: 2024,
    j1939_pgns: [],
    additional_settings: {
      dont_decode_choices: false,
      add_suffix_canid: false,
      lower_threshold: 15000, // cycle time × 15
      upper_threshold: 40000, // cycle time × 40
      dbc_ver: undefined,
    },
  });
  const [inputTableOptions, setInputTableOptions] = useState<
    {
      key: string;
      value: string;
      text: string;
    }[]
  >([]);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(true);

  // Duplicate signals state
  const [duplicateSignals, setDuplicateSignals] = useState<
    DuplicateSignalInfo[]
  >([]);
  const [allowDuplicateSignals, setAllowDuplicateSignals] =
    useState<boolean>(false);
  const [isParsingDBC, setIsParsingDBC] = useState<boolean>(false);
  const [parsingError, setParsingError] = useState<string | null>(null);

  const { selectedDBC } = props;
  const isRunning = selectedDBC?.status === "started";
  const isPeriodDifferent = selectedDBC?.period !== newDBC?.period;

  // Reset functions for better code organization
  const resetParsingStates = useCallback(() => {
    setParsingError(null);
    setDuplicateSignals([]);
    setAllowDuplicateSignals(false);
    setIsParsingDBC(false);
  }, []);

  // destructure for better code readability
  const add_suffix_canid = newDBC.additional_settings?.add_suffix_canid;
  const dont_decode_choices = newDBC.additional_settings?.dont_decode_choices;
  const upper_threshold = newDBC.additional_settings?.upper_threshold;
  const lower_threshold = newDBC.additional_settings?.lower_threshold;
  const dbc_ver = newDBC.additional_settings?.dbc_ver;

  const dbcTypes: DBCTypes[] = ["custom", "obd", "j1939"];

  const disableEnumParsingAndSuffixCanId =
    props.operationType === DBCOperationType.Edit ||
    newDBC.dbc_type !== "custom";

  // When allowDuplicateSignals is true, the add_suffix_canid checkbox should be disabled
  const isAddSuffixCanIdDisabled =
    disableEnumParsingAndSuffixCanId || allowDuplicateSignals;

  // When allowDuplicateSignals is true, automatically enable add_suffix_canid
  // When allowDuplicateSignals is false, allow user to control add_suffix_canid
  useEffect(() => {
    if (allowDuplicateSignals && newDBC.dbc_type === "custom") {
      setNewDBC((prevDBC) => ({
        ...prevDBC,
        additional_settings: {
          ...prevDBC.additional_settings,
          add_suffix_canid: true,
        },
      }));
    }
  }, [allowDuplicateSignals, newDBC.dbc_type]);

  const resetNewDBCState = () => {
    setNewDBC({
      dbc_type: "custom",
      name: "",
      version: "",
      input_table: "",
      output_table: "",
      period: 1000,
      obd_can_id: 2024,
      j1939_pgns: [],
      additional_settings: {
        dont_decode_choices: false,
        add_suffix_canid: false,
        lower_threshold: 15000, // cycle time × 15
        upper_threshold: 40000, // cycle time × 40
        dbc_ver: undefined,
      },
    });
    setActiveIndex(-1);
  };

  const setDBCType = (type: DBCTypes) => {
    setNewDBC({
      ...newDBC,
      dbc_type: type,
      ...(type === "obd" ? { j1939_pgns: [] } : {}),
      ...(type === "j1939" ? { obd_can_id: 2024 } : {}),
      ...(type !== "custom"
        ? {
            additional_settings: {
              ...newDBC.additional_settings,
              dont_decode_choices: false,
              add_suffix_canid: false,
            },
          }
        : {}),
    });
  };

  const setObdCanId = (canId: number) => {
    setNewDBC({
      ...newDBC,
      obd_can_id: canId,
    });
  };

  const setSelectedJ1939PGNs = (pgns: number[]) => {
    setNewDBC({
      ...newDBC,
      j1939_pgns: pgns,
    });
  };

  const setDBCName = (DBCName: string) => {
    setNewDBC({
      ...newDBC,
      name: DBCName,
    });
  };

  const setDBCVersion = (DBCVersion: string) => {
    setNewDBC({
      ...newDBC,
      version: DBCVersion,
    });
  };

  const setDBCInputTable = (DBCInputTableValue: string) => {
    setNewDBC({
      ...newDBC,
      input_table: DBCInputTableValue,
    });
  };

  const setDBCOutputTable = (DBCOutputTableValue: string) => {
    setNewDBC({
      ...newDBC,
      output_table: DBCOutputTableValue,
    });
  };

  const setDBCPeriod = (DBCPeriod: number) => {
    setNewDBC({
      ...newDBC,
      period: DBCPeriod,
      additional_settings: {
        ...newDBC.additional_settings,
        lower_threshold: DBCPeriod * 15,
        upper_threshold: DBCPeriod * 40,
      },
    });
  };

  const updateSuffixCanidState = (state: boolean) => {
    setNewDBC({
      ...newDBC,
      additional_settings: {
        ...newDBC.additional_settings,
        add_suffix_canid: state,
      },
    });
  };

  const updateDisableEnumParsingState = (state: boolean) => {
    setNewDBC({
      ...newDBC,
      additional_settings: {
        ...newDBC.additional_settings,
        dont_decode_choices: state,
      },
    });
  };

  const setDBCLowerThreshold = (DBCLowerThreshold: number | undefined) => {
    setNewDBC({
      ...newDBC,
      additional_settings: {
        ...newDBC.additional_settings,
        lower_threshold: DBCLowerThreshold,
      },
    });
  };

  const setDBCUpperThreshold = (DBCUpperThreshold: number | undefined) => {
    setNewDBC({
      ...newDBC,
      additional_settings: {
        ...newDBC.additional_settings,
        upper_threshold: DBCUpperThreshold,
      },
    });
  };

  const setDBCVersionFilter = (DBCVersionFilter: number | undefined) => {
    setNewDBC({
      ...newDBC,
      additional_settings: {
        ...newDBC.additional_settings,
        dbc_ver: DBCVersionFilter,
      },
    });
  };

  const handleAccordionClick = () => {
    setActiveIndex((index) => (index === 0 ? 1 : 0));
  };

  // stream validity
  // only allowed to contain underscore as special character without spaces
  const isStreamValid = (output_stream: string) => {
    const regex = /^(?=.*[a-zA-Z])\w+$/;
    return regex.test(output_stream);
  };

  const isStreamUnique = (output_stream: string) => {
    if (props.operationType === DBCOperationType.Edit) return true;
    return !allStreams.current.find((stream) => stream === output_stream);
  };

  function isNumeric(input: string): boolean {
    // Regular expression to match numeric values
    const numericRegex = /^-?(?!-)\d*\.?\d+$/;
    return numericRegex.test(input);
  }

  const isFieldPresentInStream = (field: string): boolean => {
    const input_table = newDBC.input_table;
    const validDBCStreams = validStreams.current;

    return Object.keys(validDBCStreams).some(
      (stream: string) =>
        stream === input_table && // get the selected stream &&
        // check if "dbc_ver" field is present in the stream
        Object.keys(validDBCStreams[stream]).includes(field)
    );
  };

  const handleAdditionalSettingsValidation = (): {
    isAdditionalSettingsValid: boolean;
    additional_settings: DBCAdditionalSettings;
  } => {
    let additional_settings = newDBC.additional_settings;
    let isAdditionalSettingsValid = true;

    // Check if additional_settings is defined
    if (additional_settings === undefined || additional_settings === null) {
      additional_settings = {};
    }
    const { lower_threshold, upper_threshold, dbc_ver } = additional_settings;

    // Calculate default thresholds based on cycle time (period)
    const defaultLowerThreshold = newDBC.period * 15; // cycle time × 15
    const defaultUpperThreshold = newDBC.period * 40; // cycle time × 40

    // ------------------------ Handle thresholds validation ---------------------------
    if (lower_threshold === undefined || lower_threshold === null) {
      delete additional_settings.lower_threshold;
    } else if (!isNumeric(lower_threshold.toString())) {
      isAdditionalSettingsValid = false;
      beamtoast.error("Please enter a valid lower threshold value!");
    } else if (lower_threshold <= 0) {
      isAdditionalSettingsValid = false;
      beamtoast.error("Lower Threshold cannot be negative or zero!");
    } else if (upper_threshold === undefined || upper_threshold === null) {
      // Only lower threshold is provided - validate against default upper threshold
      if (lower_threshold >= defaultUpperThreshold) {
        isAdditionalSettingsValid = false;
        beamtoast.error(
          `Lower Threshold (${lower_threshold}ms) cannot be greater than or equal to the default upper threshold (${defaultUpperThreshold}ms)`
        );
      }
    }

    if (upper_threshold === undefined || upper_threshold === null) {
      delete additional_settings.upper_threshold;
    } else if (!isNumeric(upper_threshold.toString())) {
      isAdditionalSettingsValid = false;
      beamtoast.error("Please enter a valid upper threshold value!");
    } else if (upper_threshold <= 0) {
      isAdditionalSettingsValid = false;
      beamtoast.error("Upper Threshold cannot be negative or zero!");
    } else if (lower_threshold === undefined || lower_threshold === null) {
      // Only upper threshold is provided - validate against default lower threshold
      if (upper_threshold <= defaultLowerThreshold) {
        isAdditionalSettingsValid = false;
        beamtoast.error(
          `Upper Threshold (${upper_threshold}ms) cannot be less than or equal to the default lower threshold (${defaultLowerThreshold}ms)`
        );
      }
    }

    if (
      lower_threshold &&
      upper_threshold &&
      isNumeric(lower_threshold.toString()) &&
      isNumeric(upper_threshold.toString()) &&
      lower_threshold >= upper_threshold
    ) {
      isAdditionalSettingsValid = false;
      beamtoast.error(
        "Lower Threshold cannot be equal or greater than upper threshold!"
      );
    }
    // -----------------------------------------------------------------------------------

    // ------------------------ Handle dbc version validation ----------------------------
    /*
     * validation for dbc_ver:
     ** cannot be < 0
     ** "dbc_ver" field should be present in the selected stream
     */
    if (dbc_ver === undefined || dbc_ver === null) {
      delete additional_settings.dbc_ver;
    } else if (dbc_ver < 0) {
      isAdditionalSettingsValid = false;
      beamtoast.error("Version Filter cannot be negative!");
    } else if (!isFieldPresentInStream("dbc_ver")) {
      isAdditionalSettingsValid = false;
      beamtoast.error(
        "Cannot pass dbc_ver as its column is not present in the selected stream!"
      );
    }

    return { isAdditionalSettingsValid, additional_settings };
  };

  const createJ1939_OBDParser = async (
    dbcType: string,
    additional_settings: DBCAdditionalSettings
  ) => {
    let formData = new FormData();
    formData.append("dbc_type", dbcType);

    if (dbcType === "obd") {
      formData.append("can_id", (newDBC.obd_can_id as number).toString());
    } else if (dbcType === "j1939") {
      formData.append("pgns", JSON.stringify(newDBC.j1939_pgns as number[]));
    }

    if (
      newDBC.name &&
      newDBC.version &&
      newDBC.input_table &&
      newDBC.output_table &&
      newDBC.period &&
      additional_settings
    ) {
      formData.append("name", newDBC.name.trim());
      formData.append("version", newDBC.version.trim());
      formData.append("input_table", newDBC.input_table);
      formData.append("output_table", newDBC.output_table.trim());
      formData.append("period", newDBC.period.toString().trim());
      formData.append(
        "additional_settings",
        JSON.stringify(additional_settings)
      );
      formData.append("allow_duplicate_signals", "false"); // OBD and J1939 don't have duplicate signals
    } else {
      beamtoast.error("All fields are required.");
      return;
    }

    if (
      dbcType === "obd" &&
      ((newDBC.obd_can_id as number) < 0 ||
        (newDBC.obd_can_id as number) > 536870911)
    ) {
      beamtoast.error(
        "Invalid CAN ID! Please enter a CAN ID between 0 - 536870911"
      );
      return;
    }

    if (dbcType === "j1939" && newDBC.j1939_pgns?.length === 0) {
      beamtoast.error("Please select at least one PGN");
      return;
    }

    try {
      setIsEditOrCreateDBCGoingOn(true);

      const res = await createDBCParser(formData);
      if (res.status === 201) {
        beamtoast.success(`Created DBC "${newDBC.name}"`);
        Mixpanel.track("Created DBC", {
          DBC: newDBC.version,
        });
        props.setNewDBCOutputStream(
          newDBC.output_table ? newDBC.output_table : ""
        );
        props.onClose();
        props.handleUpdate();
      }
    } catch (error: any) {
      console.log(error);
      beamtoast.error("Something went wrong! Please try again.");
    } finally {
      setIsEditOrCreateDBCGoingOn(false);
    }
  };

  const createParser = async (additional_settings: DBCAdditionalSettings) => {
    if (newDBC.dbc_type === "custom" && uploadDBCRef.current) {
      await uploadDBCRef.current?.handleUpload(additional_settings);
    } else if (newDBC.dbc_type === "obd") {
      await createJ1939_OBDParser("obd", additional_settings);
    } else if (newDBC.dbc_type === "j1939") {
      await createJ1939_OBDParser("j1939", additional_settings);
    }
  };

  const editDBC = useCallback(
    async (additional_settings: DBCAdditionalSettings) => {
      // need to keep this check here itself due to ts strict typechecking
      if (
        newDBC.name &&
        newDBC.version &&
        newDBC.input_table &&
        newDBC.output_table &&
        newDBC.period &&
        additional_settings
      ) {
        if (selectedDBC) {
          try {
            setLoading(true);
            setIsEditOrCreateDBCGoingOn(true);
            await updateDBCSettings(selectedDBC.id, {
              name: newDBC.name.trim(),
              period: newDBC.period,
              version: newDBC.version.trim(),
              additional_settings: {
                dbc_ver: additional_settings.dbc_ver ?? null,
                lower_threshold:
                  additional_settings.lower_threshold ?? newDBC.period * 15, // cycle time × 15
                upper_threshold:
                  additional_settings.upper_threshold ?? newDBC.period * 40, // cycle time × 40
              },
            });

            beamtoast.success(`Updated DBC "${newDBC.name}" Successfully`);
            Mixpanel.track("Updated DBC", {
              Firmware: newDBC.version,
            });
            props.setNewDBCOutputStream(
              newDBC.output_table ? newDBC.output_table : ""
            );
            setLoading(false);
            onModalClose();
            props.handleUpdate();
          } catch (error: any) {
            setLoading(false);
            console.log(error);

            Mixpanel.track("Failure", {
              type: "Upload DBC",
            });
            props.setNewDBCOutputStream("");

            // Handling other error types
            const errorMessage = error.response?.data?.error;
            console.log(errorMessage); // print error message received from api error response

            if (errorMessage) {
              beamtoast.error(errorMessage);
            } else {
              beamtoast.error("Failed to update DBC. Please try again.");
            }
          } finally {
            setIsEditOrCreateDBCGoingOn(false);
          }
        }
      } else {
        beamtoast.error("All fields are required.");
      }
    },
    [selectedDBC, newDBC] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const stopAndUpdateDBCParser = async () => {
    if (selectedDBC) {
      try {
        setLoading(true);
        await stopDBCParser(selectedDBC?.id);
        editDBC(newDBC.additional_settings ?? {});
      } catch (e) {
        beamtoast.error(`Failed to stop DBC Parser "${selectedDBC.name}"`);
        setLoading(false);
        console.log(e);
      }
    }
  };

  const haveAdditionalSettingsChanged = () => {
    const selectedDBCAdditionalSettings = {
      add_suffix_canid:
        selectedDBC?.additional_settings?.add_suffix_canid ?? false,
      dont_decode_choices:
        selectedDBC?.additional_settings?.dont_decode_choices ?? false,
      lower_threshold:
        selectedDBC?.additional_settings?.lower_threshold ?? null,
      upper_threshold:
        selectedDBC?.additional_settings?.upper_threshold ?? null,
      dbc_ver: selectedDBC?.additional_settings?.dbc_ver ?? null,
      add_date_filter:
        selectedDBC?.additional_settings?.add_date_filter ?? false,
    };
    const newDBCAdditionalSettings = {
      add_suffix_canid: newDBC?.additional_settings?.add_suffix_canid ?? false,
      dont_decode_choices:
        newDBC?.additional_settings?.dont_decode_choices ?? false,
      lower_threshold: newDBC?.additional_settings?.lower_threshold ?? null,
      upper_threshold: newDBC?.additional_settings?.upper_threshold ?? null,
      dbc_ver: newDBC?.additional_settings?.dbc_ver ?? null,
      add_date_filter: newDBC?.additional_settings?.add_date_filter ?? false,
    };

    return _.isEqual(selectedDBCAdditionalSettings, newDBCAdditionalSettings);
  };

  const areAdditionalSettingsEqual = haveAdditionalSettingsChanged();

  const editParser = async (additional_settings: DBCAdditionalSettings) => {
    // check if parser is already running and restart
    // only if cycle time or any additional settings have been changed

    // check for any overall updates else do not trigger the update-api without any need
    const {
      additional_settings: as1,
      id,
      tenant_id,
      status,
      offsets,
      ...prevRest
    } = selectedDBC ?? {};
    const { additional_settings: as2, ...updatedRest } = newDBC;

    if (areAdditionalSettingsEqual && _.isEqual(prevRest, updatedRest)) {
      beamtoast.error("No values have been updated!");
      return;
    }

    // Show restart warning for changes that require parser restart
    if (isRunning && (isPeriodDifferent || !areAdditionalSettingsEqual)) {
      stopAndUpdateDBCParser();
    } else {
      editDBC(additional_settings);
    }
  };

  const canIdSufficePopupContent = (): string => {
    let content =
      "Adds CAN ID suffix to signal names, creating unique column names in database table";

    if (newDBC.dbc_type === "custom") {
      if (props.operationType === DBCOperationType.Edit) {
        content =
          "Cannot change after creation, changing this affects database table schema and data inconsistency between old and new records";
      } else if (allowDuplicateSignals) {
        content =
          "Auto-enabled for duplicate signals to prevent column name conflicts";
      }
    } else {
      content = `${newDBC.dbc_type === "obd" ? "OBD" : "J1939"} signals are unique, no suffix needed`;
    }

    return content;
  };

  const disableEnumParsingPopupContent = (): string => {
    let content =
      "Stores enum values as raw numbers instead of text representations in database table";

    if (newDBC.dbc_type === "custom") {
      if (props.operationType === DBCOperationType.Edit) {
        content =
          "Cannot change after creation, affects enum processing in database table, toggling would cause data inconsistency between old and new records";
      }
    } else {
      content = `${newDBC.dbc_type === "obd" ? "OBD" : "J1939"} enum parsing is predefined and cannot be modified`;
    }

    return content;
  };

  const handleSubmit = async () => {
    // output_stream value validation acc to regex
    if (!(newDBC.output_table && isStreamValid(newDBC.output_table))) {
      beamtoast.error(
        "Please enter a valid output stream name! Space and special characters are not allowed except underscore"
      );
      return;
    }

    if (!(newDBC.output_table && isStreamUnique(newDBC.output_table))) {
      beamtoast.error("Output stream name already exists!");
      return;
    }

    // period validation (cannot be <= 0)
    if (newDBC.period <= 0) {
      beamtoast.error("Cycle time cannot be negative or zero!");
      return;
    }

    // additional settings validation
    const { additional_settings, isAdditionalSettingsValid } =
      handleAdditionalSettingsValidation();
    if (!isAdditionalSettingsValid) return;

    try {
      if (props.operationType === DBCOperationType.Create) {
        await createParser(additional_settings);
      } else if (props.operationType === DBCOperationType.Edit) {
        await editParser(additional_settings);
      }
    } catch (error: any) {
      console.log(error);
      beamtoast.error("Something went wrong! Please try again.");
    }
  };

  const onModalClose = () => {
    // Reset the state of the modal
    setDBCType("custom");
    setLoading(false);
    setIsEditOrCreateDBCGoingOn(false);
    resetNewDBCState();
    setInputTableOptions([]);
    setButtonDisabled(true);
    setObdCanId(2024);
    setSelectedJ1939PGNs([]);

    // Reset duplicate signals and warning states
    resetParsingStates();

    // Close the modal
    props.onClose();
  };

  useEffect(() => {
    const fn = async () => {
      let streamNames: string[] = [];
      const requiredFields = [
        "can_id",
        "byte1",
        "byte2",
        "byte3",
        "byte4",
        "byte5",
        "byte6",
        "byte7",
        "byte8",
        "sequence",
        "timestamp",
      ];

      const streams = await fetchAllStreamsWithDetails();

      for (const stream in streams) {
        const fields = streams[stream].fields;
        allStreams.current.push(stream);

        // check all mentioned fields above are present in the stream
        const allRequiredFieldsPresent = requiredFields.every((requiredField) =>
          Object.keys(fields).includes(requiredField)
        );

        if (allRequiredFieldsPresent) {
          /*
           * update valid streams list:
           ** validStreams contains list of streams with their data
           ** streamNames only contains a list of names of the valid streams
           */
          validStreams.current[stream] = streams[stream].fields;
          streamNames.push(stream);
        }
      }

      const tableOptions = dropDownOptionsFromArray(streamNames);
      setInputTableOptions(tableOptions);
    };

    fn();
  }, [props.open]); // eslint-disable-line react-hooks/exhaustive-deps

  // disable submit button until all compulsory data is filled
  useEffect(() => {
    function areAllKeysNotEmpty(dbc: DBCData): boolean {
      return Object.entries(dbc).every(([key, value]) => {
        if (key === "additional_settings") {
          return true; // Skip checking this key as it is not mandatory
        } else {
          return value || value === null || value === 0;
        }
      });
    }

    if (areAllKeysNotEmpty(newDBC)) {
      setButtonDisabled(false);
    } else {
      setButtonDisabled(true);
    }
  }, [newDBC, props.open]);

  useEffect(() => {
    // Reset duplicate signals state when modal opens
    if (props.open) {
      resetParsingStates();
    }

    const {
      selectedDBC: {
        dbc_type = "custom",
        name = "",
        version = "",
        input_table = "",
        output_table = "",
        period = 1000,
        additional_settings = {},
        obd_can_id = 2024,
        j1939_pgns = [],
      } = {},
    } = props;

    const {
      dont_decode_choices = false,
      add_suffix_canid = false,
      lower_threshold = period * 15, // cycle time × 15
      upper_threshold = period * 40, // cycle time × 40
      dbc_ver = undefined,
    } = additional_settings || {};

    setNewDBC({
      dbc_type,
      name,
      version,
      input_table,
      output_table,
      period,
      obd_can_id,
      j1939_pgns,
      additional_settings: {
        dont_decode_choices,
        add_suffix_canid,
        lower_threshold,
        upper_threshold,
        dbc_ver,
      },
    });
  }, [props.selectedDBC, props.open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Modal
        className="dark"
        onClose={onModalClose}
        onOpen={props.onOpen}
        open={props.open}
      >
        <Modal.Header>
          {props.operationType === DBCOperationType.Create ? "Create" : "Edit"}{" "}
          DBC Parser
        </Modal.Header>
        <Modal.Content>
          {loading ? (
            <LoadingAnimation
              loaderContainerHeight="350px"
              fontSize="1.25rem"
              loadingText="Loading..."
              loaderSize="42px"
              loaderBorderSize="4px"
            />
          ) : (
            <Grid>
              <Grid.Row>
                <Grid.Column width={8}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>DBC Type</StyledLabel>
                    <Dropdown
                      selection
                      fluid
                      search
                      placeholder="DBC Type"
                      options={dbcTypes.map((type) => ({
                        key: type,
                        value: type,
                        text: type,
                      }))}
                      value={newDBC.dbc_type}
                      disabled={props.operationType === DBCOperationType.Edit}
                      onChange={(_e, d) => {
                        if (d.value) setDBCType(d.value.toString() as DBCTypes);
                      }}
                      style={{
                        border: "none",
                      }}
                    />
                  </StyledInput>
                </Grid.Column>
              </Grid.Row>

              {/* Custom DBC Type  */}
              {newDBC.dbc_type === "custom" &&
                props.operationType === DBCOperationType.Create && (
                  <Grid.Row>
                    <Grid.Column width={8}>
                      <UploadDBCFileSection
                        ref={uploadDBCRef}
                        onClose={onModalClose}
                        DBCData={props.DBCData}
                        newDBC={newDBC}
                        setIsEditOrCreateDBCGoingOn={
                          setIsEditOrCreateDBCGoingOn
                        }
                        setNewDBCOutputStream={(name: string) =>
                          props.setNewDBCOutputStream(name)
                        }
                        operationType={props.operationType}
                        handleUpdate={() => props.handleUpdate()}
                        duplicateSignals={duplicateSignals}
                        setDuplicateSignals={setDuplicateSignals}
                        allowDuplicateSignals={allowDuplicateSignals}
                        setAllowDuplicateSignals={setAllowDuplicateSignals}
                        isParsingDBC={isParsingDBC}
                        setIsParsingDBC={setIsParsingDBC}
                        parsingError={parsingError}
                        setParsingError={setParsingError}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}

              {/* Parsing Status and Errors - Show in main modal */}
              {newDBC.dbc_type === "custom" && isParsingDBC && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message info>
                      <MessageHeader>
                        <Icon name="spinner" loading />
                        Parsing DBC File
                      </MessageHeader>
                      <MessageList>
                        <MessageItem>
                          Analyzing DBC file for duplicate signals...
                        </MessageItem>
                      </MessageList>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              )}

              {newDBC.dbc_type === "custom" && parsingError && (
                <Grid.Row>
                  <Grid.Column width={16}>
                    <Message error>
                      <MessageHeader>
                        <Icon name="exclamation triangle" />
                        Parsing Error
                      </MessageHeader>
                      <MessageList>
                        <MessageItem>{parsingError}</MessageItem>
                      </MessageList>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              )}

              {/* Duplicate Signals Warning - Show in main modal */}
              {newDBC.dbc_type === "custom" &&
                duplicateSignals.length > 0 &&
                !isParsingDBC &&
                !parsingError && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <DuplicateSignalsWarning
                        duplicates={duplicateSignals}
                        allowDuplicates={allowDuplicateSignals}
                        onAllowDuplicatesChange={setAllowDuplicateSignals}
                      />
                    </Grid.Column>
                  </Grid.Row>
                )}

              {/* OBD DBC Type */}
              {newDBC.dbc_type === "obd" && (
                <Grid.Row>
                  <Grid.Column width={8}>
                    <StyledInput labelPosition="left">
                      <StyledLabel>CAN ID</StyledLabel>
                      <input
                        type="number"
                        placeholder="CAN ID of the message"
                        value={newDBC.obd_can_id as number}
                        onChange={(e) =>
                          setObdCanId(parseInt(e.target.value || "0"))
                        }
                        disabled={props.operationType === DBCOperationType.Edit}
                      />
                    </StyledInput>
                  </Grid.Column>
                </Grid.Row>
              )}

              {/* J1939 DBC Type */}
              {newDBC.dbc_type === "j1939" && (
                <Grid.Row>
                  <Grid.Column width={8}>
                    <StyledInput labelPosition="left">
                      <StyledLabel>Select PGNs</StyledLabel>
                      <Dropdown
                        selection
                        fluid
                        multiple
                        search
                        placeholder="Select PGNs"
                        options={pgnsWithLessThan8bytes.map((pgn) => ({
                          key: pgn,
                          value: pgn,
                          text: pgn,
                        }))}
                        value={newDBC.j1939_pgns as number[]}
                        disabled={props.operationType === DBCOperationType.Edit}
                        onChange={(_e, d) => {
                          let values = d.value as number[];
                          if (values?.length > 20) {
                            beamtoast.error("Cannot select more than 20 PGNs");
                          } else if (values) setSelectedJ1939PGNs(values);
                        }}
                        style={{
                          border: "none",
                        }}
                      />
                    </StyledInput>
                  </Grid.Column>
                </Grid.Row>
              )}

              <ThinDivider />

              <Grid.Row>
                <Grid.Column width={8}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>DBC Name</StyledLabel>
                    <input
                      placeholder="DBC Name"
                      value={newDBC.name}
                      onChange={(e) => setDBCName(e.target.value)}
                    />
                  </StyledInput>
                </Grid.Column>
                <Grid.Column width={8}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>DBC Version</StyledLabel>
                    <input
                      placeholder="DBC Version"
                      value={newDBC.version}
                      onChange={(e) => setDBCVersion(e.target.value)}
                    />
                  </StyledInput>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={8}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>
                      Input Stream
                      <Popup
                        content={"This Stream Can No Longer Be Updated"}
                        position="top center"
                        inverted
                        trigger={
                          <Icon
                            style={{
                              marginLeft: "8px",
                              justifyContent: "center",
                            }}
                            name="info circle"
                          />
                        }
                      />
                    </StyledLabel>
                    <Dropdown
                      selection
                      fluid
                      search
                      placeholder="Stream name"
                      options={inputTableOptions}
                      value={newDBC.input_table}
                      disabled={props.operationType === DBCOperationType.Edit}
                      onChange={(_e, d) => {
                        if (d.value) setDBCInputTable(d.value.toString());
                      }}
                      style={{
                        border: "none",
                      }}
                    />
                  </StyledInput>
                </Grid.Column>
                <Grid.Column width={8}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>Output Stream</StyledLabel>
                    <input
                      placeholder="Output Stream"
                      value={newDBC.output_table}
                      disabled={props.operationType === DBCOperationType.Edit}
                      onChange={(e) => setDBCOutputTable(e.target.value)}
                    />
                  </StyledInput>
                </Grid.Column>
              </Grid.Row>
              <Grid.Row>
                <Grid.Column width={16}>
                  <StyledInput labelPosition="left">
                    <StyledLabel>Cycle Time (ms)</StyledLabel>
                    <input
                      placeholder="Cycle Time (ms)"
                      type="number"
                      value={newDBC.period}
                      onChange={(e) => setDBCPeriod(Number(e.target.value))}
                    />
                  </StyledInput>
                </Grid.Column>
              </Grid.Row>

              {isRunning &&
                (isPeriodDifferent || !areAdditionalSettingsEqual) &&
                props.operationType === DBCOperationType.Edit && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message warning>
                        <MessageList>
                          <MessageItem>
                            Note: Changes to cycle time or additional settings
                            require the parser to be restarted. The parser will
                            be stopped and need to be started manually.
                          </MessageItem>
                        </MessageList>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                )}

              {((!isAdmin && newDBC.name && newDBC.output_table) ||
                newDBC.input_table) &&
                props.operationType === DBCOperationType.Create && (
                  <Grid.Row>
                    <Grid.Column width={16}>
                      <Message warning>
                        <MessageList>
                          {newDBC.input_table && (
                            <MessageItem>
                              {`Note: Selected input stream(${newDBC.input_table}) for DBC parsing will no longer be editable once the DBC Parser is created.`}
                            </MessageItem>
                          )}
                          {!isAdmin && newDBC.name && newDBC.output_table && (
                            <MessageItem>
                              {`Note: Contact admin for access to output stream(${newDBC.output_table}) created by the DBC parser (${newDBC.name}) to verify output data`}
                            </MessageItem>
                          )}
                        </MessageList>
                      </Message>
                    </Grid.Column>
                  </Grid.Row>
                )}
              <ThinDivider />

              <Grid.Row>
                <Accordion inverted>
                  <Accordion.Title
                    active={activeIndex === 0}
                    index={0}
                    onClick={handleAccordionClick}
                  >
                    <Icon name="dropdown" />
                    <span style={{ fontWeight: "bold", fontSize: "1.2rem" }}>
                      Additional Settings
                    </span>
                  </Accordion.Title>
                  <Accordion.Content
                    active={activeIndex === 0}
                    style={{ paddingLeft: "20px" }}
                  >
                    <Grid style={{ width: "100%" }}>
                      <Grid.Row style={{ marginTop: "1rem" }}>
                        <Grid.Column width={16}>
                          <Checkbox
                            checked={add_suffix_canid}
                            style={{ marginRight: "1rem" }}
                            disabled={isAddSuffixCanIdDisabled}
                            onChange={(e, { checked }) => {
                              updateSuffixCanidState(checked ?? false);
                            }}
                          />
                          <span
                            style={{
                              fontSize: "1.1rem",
                              cursor: isAddSuffixCanIdDisabled
                                ? "not-allowed"
                                : "text",
                              opacity: isAddSuffixCanIdDisabled ? 0.5 : 1,
                            }}
                          >
                            Add CAN ID as suffix to column names
                          </span>
                          <Popup
                            content={canIdSufficePopupContent()}
                            position="top center"
                            inverted
                            trigger={
                              <Icon
                                style={{
                                  marginLeft: "8px",
                                  justifyContent: "center",
                                }}
                                name="info circle"
                              />
                            }
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Checkbox
                            checked={dont_decode_choices}
                            style={{ marginRight: "1rem" }}
                            disabled={disableEnumParsingAndSuffixCanId}
                            onChange={(e, { checked }) => {
                              updateDisableEnumParsingState(checked ?? false);
                            }}
                          />
                          <span
                            style={{
                              fontSize: "1.1rem",
                              cursor: disableEnumParsingAndSuffixCanId
                                ? "not-allowed"
                                : "text",
                              opacity: disableEnumParsingAndSuffixCanId
                                ? 0.5
                                : 1,
                            }}
                          >
                            Disable Enum Parsing
                          </span>
                          <Popup
                            content={disableEnumParsingPopupContent()}
                            position="top center"
                            inverted
                            trigger={
                              <Icon
                                style={{
                                  marginLeft: "8px",
                                  justifyContent: "center",
                                }}
                                name="info circle"
                              />
                            }
                          />
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <StyledInput labelPosition="left">
                            <StyledLabel>
                              Lower Threshold for flushing clusters (ms)
                              <Popup
                                content={`Default value: ${newDBC.period * 15}ms (cycle time * 15)`}
                                position="top center"
                                inverted
                                trigger={
                                  <Icon
                                    style={{
                                      marginLeft: "8px",
                                      justifyContent: "center",
                                    }}
                                    name="info circle"
                                  />
                                }
                              />
                            </StyledLabel>
                            <input
                              placeholder="value in ms"
                              type="number"
                              value={
                                !lower_threshold
                                  ? newDBC.period * 15
                                  : lower_threshold
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                ) {
                                  setDBCLowerThreshold(newDBC.period * 15);
                                } else {
                                  setDBCLowerThreshold(Number(value));
                                }
                              }}
                            />
                          </StyledInput>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <StyledInput labelPosition="left">
                            <StyledLabel>
                              Upper Threshold for flushing clusters (ms)
                              <Popup
                                content={`Default value: ${newDBC.period * 40}ms (cycle time * 40)`}
                                position="top center"
                                inverted
                                trigger={
                                  <Icon
                                    style={{
                                      marginLeft: "8px",
                                      justifyContent: "center",
                                    }}
                                    name="info circle"
                                  />
                                }
                              />
                            </StyledLabel>
                            <input
                              placeholder="value in ms"
                              type="number"
                              value={
                                !upper_threshold
                                  ? newDBC.period * 40
                                  : upper_threshold
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                if (
                                  value === "" ||
                                  value === null ||
                                  value === undefined
                                ) {
                                  setDBCUpperThreshold(newDBC.period * 40);
                                } else {
                                  setDBCUpperThreshold(Number(value));
                                }
                              }}
                            />
                          </StyledInput>
                        </Grid.Column>
                      </Grid.Row>
                      <Grid.Row>
                        <Grid.Column width={12}>
                          <StyledInput labelPosition="left">
                            <StyledLabel>
                              DBC Version filter in CAN table on column dbc_ver
                            </StyledLabel>
                            <input
                              placeholder="value"
                              type="number"
                              value={dbc_ver ?? undefined}
                              onChange={(e) =>
                                setDBCVersionFilter(
                                  e.target.value
                                    ? parseInt(e.target.value)
                                    : undefined
                                )
                              }
                            />
                          </StyledInput>
                        </Grid.Column>
                      </Grid.Row>
                    </Grid>
                  </Accordion.Content>
                </Accordion>
              </Grid.Row>
            </Grid>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button
            secondary
            onClick={() => {
              onModalClose();
            }}
          >
            Cancel
          </Button>

          <Button
            type="submit"
            primary
            disabled={buttonDisabled || isEditOrCreateDBCGoingOn}
            loading={isEditOrCreateDBCGoingOn}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );
}
